import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  createOperatorSubRole,
  getOperatorSubRoleDefaultPackageIds,
  getOperatorSubRoles,
  saveOperatorSubRoleDefaultPackageIds,
  updateOperatorSubRole,
  type OperatorSubRoleUpsertRequest,
} from '../core/_requests'
import { getAdminAccessBundles, type AdminAccessBundle } from '../core/accessRequests'
import type { OperatorSubRole } from '../core/_models'

const blankForm: OperatorSubRoleUpsertRequest = {
  code: '',
  name: '',
  description: '',
  sortOrder: 0,
  active: true,
}

export const OperatorSubRolesManagerPanel: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [subRoles, setSubRoles] = useState<OperatorSubRole[]>([])
  const [bundles, setBundles] = useState<AdminAccessBundle[]>([])
  const [selectedSubRoleId, setSelectedSubRoleId] = useState<number | null>(null)
  const [selectedBundleIds, setSelectedBundleIds] = useState<number[]>([])
  const [form, setForm] = useState<OperatorSubRoleUpsertRequest>(blankForm)

  const loadAll = async () => {
    setLoading(true)
    try {
      const [roles, packageRows] = await Promise.all([getOperatorSubRoles(), getAdminAccessBundles()])
      setSubRoles(roles || [])
      setBundles((packageRows || []).filter((b) => b.active))
      const firstId = roles?.[0]?.id ?? null
      setSelectedSubRoleId(firstId)
    } catch (e: any) {
      toast.error('Failed to load operator sub-roles')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAll()
  }, [])

  useEffect(() => {
    if (!selectedSubRoleId) {
      setSelectedBundleIds([])
      return
    }
    const hit = subRoles.find((r) => Number(r.id) === Number(selectedSubRoleId))
    setForm({
      code: hit?.code || '',
      name: hit?.name || '',
      description: hit?.description || '',
      sortOrder: hit?.sortOrder ?? 0,
      active: hit?.active ?? true,
    })
    getOperatorSubRoleDefaultPackageIds(selectedSubRoleId)
      .then((rows) => setSelectedBundleIds(rows || []))
      .catch((e: any) => toast.error('Failed to load default packages'))
  }, [selectedSubRoleId, subRoles])

  const submitForm = async () => {
    const payload: OperatorSubRoleUpsertRequest = {
      code: String(form.code || '').trim(),
      name: String(form.name || '').trim(),
      description: String(form.description || '').trim() || null,
      sortOrder: Number(form.sortOrder || 0),
      active: form.active !== false,
    }
    if (!payload.code || !payload.name) {
      toast.error('Code and sub-role name are required.')
      return
    }
    setSaving(true)
    try {
      if (selectedSubRoleId) {
        await updateOperatorSubRole(selectedSubRoleId, payload)
        toast.success('Operator sub-role updated.')
      } else {
        await createOperatorSubRole(payload)
        toast.success('Operator sub-role created.')
      }
      await loadAll()
    } catch (e: any) {
      toast.error('Failed to save operator sub-role')
    } finally {
      setSaving(false)
    }
  }

  const saveDefaultPackages = async () => {
    if (!selectedSubRoleId) {
      toast.error('Select a sub-role first.')
      return
    }
    setSaving(true)
    try {
      await saveOperatorSubRoleDefaultPackageIds(selectedSubRoleId, selectedBundleIds)
      toast.success('Default package mapping saved.')
    } catch (e: any) {
      toast.error('Failed to save default package mapping')
    } finally {
      setSaving(false)
    }
  }

  const toggleBundle = (bundleId: number, checked: boolean) => {
    setSelectedBundleIds((prev) =>
      checked ? Array.from(new Set([...prev, bundleId])) : prev.filter((id) => id !== bundleId)
    )
  }

  return (
    <div className='w-100'>
      <div className='d-flex justify-content-between align-items-start mb-3'>
        <div>
          <h5 className='mb-1 text-dark'>Operator sub-roles</h5>
          <p className='text-muted small mb-0'>
            Manage operator types and their default access.
          </p>
        </div>
      </div>
      {loading ? (
        <div className='text-muted small'>Loading…</div>
      ) : (
        <div className='row g-4'>
          <div className='col-lg-4'>
            <div className='d-flex justify-content-between align-items-center mb-2'>
              <label className='form-label fw-semibold mb-0 text-dark'>Sub-roles</label>
              <button
                type='button'
                className='btn btn-sm btn-light-primary'
                onClick={() => {
                  setSelectedSubRoleId(null)
                  setForm(blankForm)
                  setSelectedBundleIds([])
                }}
              >
                New
              </button>
            </div>
            <div className='list-group' style={{ maxHeight: 380, overflowY: 'auto' }}>
              {subRoles.map((sr) => (
                <button
                  key={sr.id}
                  type='button'
                  className={`list-group-item list-group-item-action ${
                    Number(selectedSubRoleId) === Number(sr.id) ? 'active' : ''
                  }`}
                  onClick={() => setSelectedSubRoleId(sr.id)}
                >
                  <div className='d-flex justify-content-between align-items-center'>
                    <span>{sr.name}</span>
                    <span className={`badge ${sr.active === false ? 'bg-danger' : 'bg-success'}`}>
                      {sr.active === false ? 'Inactive' : 'Active'}
                    </span>
                  </div>
                  <small>{sr.code}</small>
                </button>
              ))}
            </div>
          </div>
          <div className='col-lg-8'>
            <div className='row g-3 mb-3'>
              <div className='col-md-6'>
                <label className='form-label fw-semibold text-dark'>Sub-role code</label>
                <input
                  className='form-control'
                  value={form.code || ''}
                  onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))}
                />
              </div>
              <div className='col-md-6'>
                <label className='form-label fw-semibold text-dark'>Sub-role name</label>
                <input
                  className='form-control'
                  value={form.name || ''}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className='col-md-8'>
                <label className='form-label fw-semibold text-dark'>Description</label>
                <input
                  className='form-control'
                  value={form.description || ''}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                />
              </div>
              <div className='col-md-2'>
                <label className='form-label fw-semibold text-dark'>Sort</label>
                <input
                  className='form-control'
                  type='number'
                  value={form.sortOrder ?? 0}
                  onChange={(e) => setForm((prev) => ({ ...prev, sortOrder: Number(e.target.value || 0) }))}
                />
              </div>
              <div className='col-md-2 d-flex align-items-end'>
                <label className='form-check mb-2'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    checked={form.active !== false}
                    onChange={(e) => setForm((prev) => ({ ...prev, active: e.target.checked }))}
                  />
                  <span className='form-check-label ms-2 text-dark'>Active</span>
                </label>
              </div>
            </div>

            <div className='d-flex gap-2 mb-3'>
              <button type='button' className='btn btn-primary' onClick={submitForm} disabled={saving}>
                {selectedSubRoleId ? 'Save sub-role' : 'Create sub-role'}
              </button>
            </div>

            <label className='form-label fw-semibold text-dark'>Default package / access template</label>
            <div className='border rounded p-3' style={{ maxHeight: 240, overflowY: 'auto' }}>
              {bundles.map((b) => (
                <label key={b.id} className='form-check mb-2'>
                  <input
                    type='checkbox'
                    className='form-check-input'
                    checked={selectedBundleIds.includes(b.id)}
                    onChange={(e) => toggleBundle(b.id, e.target.checked)}
                    disabled={!selectedSubRoleId}
                  />
                  <span className='form-check-label text-dark'>
                    <strong>{b.name}</strong> <span className='text-muted'>({b.code})</span>
                  </span>
                </label>
              ))}
            </div>
            <div className='d-flex gap-2 mt-3'>
              <button
                type='button'
                className='btn btn-light-primary'
                disabled={!selectedSubRoleId || saving}
                onClick={saveDefaultPackages}
              >
                Save default package mapping
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

